import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  computed,
  inject,
  input,
  signal,
} from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { SelectMenuSearchComponent } from '../table/filters/select-menu-search/select-menu-search.component'

import { SelectMenuListComponent } from '../table/filters/select-menu-list/select-menu-list.component'
import { SelectMenuListItemComponent } from '../table/filters/select-menu-list-item/select-menu-list-item.component'
import { SelectMenuListNoItemsComponent } from '../table/filters/select-menu-list-no-items-item/select-menu-list-no-items-item.component'
import { ServicePointGroupService } from 'src/app/public/service-point-groups/service-point-group.service'
import { SelectMenuListNamedSectionComponent } from '../table/filters/select-menu-list-named-section-item/select-menu-list-named-section-item.component'
import { SelectMenuListDividerComponent } from '../table/filters/select-menu-list-divider-item/select-menu-list-divider-item.component'
import { ServicePointGroupFilter } from 'src/app/shared/services/filter/service-point-group-filter'
import { ServicePointGroupWithRefs } from '../service-point-groups/service-point-group.types'
import { toSignal } from '@angular/core/rxjs-interop'

@Component({
  imports: [
    MatIcon,
    SelectMenuSearchComponent,
    SelectMenuListComponent,
    SelectMenuListItemComponent,
    SelectMenuListNamedSectionComponent,
    SelectMenuListDividerComponent,
    SelectMenuListNoItemsComponent,
  ],
  selector: 'div[app-service-point-group-selector]',
  template: `
    <div class="wrapper" [class.slim]="slim">
      <mat-icon class="search-icon" svgIcon="search"></mat-icon>
      <input
        type="button"
        [placeholder]="'none'"
        [value]="displayText"
        (click)="toggle()"
      />
      <div class="popover" [class.open]="open">
        <div
          app-select-menu-search
          name="entity_code"
          [label]="label"
          [value]="search()"
          (search)="search.set($event)"
        ></div>
        <ul app-select-menu-list>
          @for(result of selectedGroupMap(); track
          result[0]; let first = $first, last = $last) { @if(first) {
          <li app-select-menu-list-named-section-item name="Selected"></li>
          }
          <li
            app-select-menu-list-item
            [value]="result[1]"
            [display]="result[1].service_point_group_name"
            [selected]="true"
            [multi]="!!config.allowMultiple"
            (select)="setValue($event)"
          ></li>
          @if(last) {
          <li app-select-menu-list-divider-item></li>
          } }

          <!-- SHOW SEARCH RESULTS -->
          @for (result of searchResults(); track result.service_point_group_uid;)
          {
          <li
            app-select-menu-list-item
            [value]="result"
            [display]="result.service_point_group_name"
            [selected]="
              selectedGroupMap()
                ? selectedGroupMap()!.has(result.service_point_group_uid)
                : result.service_point_group_uid ===
                  value?.service_point_group_uid
            "
            [multi]="!!config.allowMultiple"
            (select)="setValue($event)"
          ></li>
          }

          <!-- SHOW NO RESULTS -->
          @if(!searchResults().length) {
          <li app-select-menu-list-no-items-item></li>
          }

        </ul>
      </div>
    </div>
  `,
  styles: `
    .wrapper {
      position: relative;
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid black;

      &.slim {
        padding: 0.5rem 0 0;
        border: none; 
      }
    }

    input {
      width: 100%;
      padding: 0.5rem 1.75rem 0.25rem 0.25rem;
      border: none;
      background: white;
      text-align: left;
      border-bottom: 1px solid black;
      font-size: 0.9rem;
      line-height: 1.25rem;

      &:focus {
        outline: none;
      }
    }

    .search-icon {
      position: absolute;
      top: 0.75rem;
      right: 0.25rem;
    }
  
    .popover {
      display: none;
      position: absolute;
    }

    .popover.open {
      z-index: 3;
      display: flex;
      flex-direction: column;
      min-height: 7rem;
      width: 300px;
      right: 0;
      top: calc(100% + 0.5rem);
      border: 1px solid black;
      border-radius: 0;
      padding: 0.5rem 1rem;
      background: white;


      &::before {
        content: ' ';
        border: none;
        border-top: 1px solid black;
        border-right: 1px solid black;
        width: 0.5rem;
        height: 0.5rem;
        transform: rotate(-45deg);
        position: absolute;
        top: calc(-0.25rem - 1px);
        background: linear-gradient(
          45deg, 
          rgba(255,255,255,0) 0%, 
          rgba(255,255,255,0) calc(50% - 1px), 
          rgba(255,255,255,1) calc(50% - 1px), 
          rgba(255,255,255,1) 100%
          );
        right: 1rem;
      }
    }
  `,
})
export class ServicePointGroupSelectorComponent {
  elementRef = inject(ElementRef)
  servicePointGroupService = inject(ServicePointGroupService)

  @Input() value: ServicePointGroupWithRefs | null = null
  @Input() label = ''
  @Input() slim = false

  selectedGroups = input<Map<string, ServicePointGroupWithRefs> | ServicePointGroupWithRefs[] | undefined>()
  selectedGroupMap = computed(() => {
    const groups = this.selectedGroups()
    if (Array.isArray(groups)) {
      return new Map(groups.map(g => [g.service_point_group_uid, g]))
    }
    return groups
  })

  @Input() config: Omit<ServicePointGroupFilter['state']['config'], 'meta'>

  @Output() valueChange = new EventEmitter<ServicePointGroupWithRefs>()

  open = false

  search = signal<string>('')
  allResults = toSignal(this.servicePointGroupService.get())
  searchResults = computed(() => (this.allResults() ?? [])
    .filter(g => g.service_point_group_name
      .toLowerCase()
      .startsWith(this.search().toLowerCase()),
    ),
  )

  get displayText() {
    if (this.selectedGroupMap()) {
      if (!this.selectedGroupMap()?.size) {
        return 'None Selected'
      }
      return this.selectedGroupMap()!.size > 1
        ? `${this.selectedGroupMap()!.size} Selected`
        : [...this.selectedGroupMap()!.values()][0]?.service_point_group_name
    }
    return this.value?.service_point_group_uid ?? 'None Selected'
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    if (
      this.open &&
      !this.elementRef.nativeElement.contains(event.target as Node)
    ) {
      this.toggle()
    }
  }


  toggle(): void {
    this.open = !this.open
    this.search.set('')
  }

  setValue(event: ServicePointGroupWithRefs) {
    this.value = event
    this.valueChange.emit(event)
    if (!this.config.allowMultiple) {
      this.open = false
    }
  }
}
